/**
 * ForecastChart の外部凡例 (checkbox 群) の系列一覧を算出する純関数。
 *
 * design 参照: design/demand/forecast-today.html L2950-2976 (`seriesList` 構築)。
 * 凡例の並び順は design 準拠:
 *   需要想定値 (area, 編集対象バッジ) → 当日実績 → 過去実績スロット
 *   ("実績 M/D"、非ゼロ埋め。テーブルヘッダと同じ formatProtoMonthDay で統一) →
 *   気温系列 (予想気温)
 * のあと、design に無いが実装済みの系列 (実績トレンド予測・気温実績・露点) を
 * 末尾に追加する。AC-03 (spec 準拠改修 demand-forecast-daily-v4): 前日実績系列は
 * 過剰実装のため削除済み (design のビルトイン固定系列だが spec 非要求)。
 * 確定履歴の比較系列 (confirmComparisons) は spec 根拠の無い常時系列だったため
 * demand-48koma-columns で全廃した (AC-07)。
 *
 * ForecastChart (Line/Area 描画可否) と本モジュール (凡例表示可否) は同じ
 * hasXxx 判定を共有する必要があるため、呼び出し元 (ForecastCard) が両方に
 * 同じ boolean を渡す。
 */
import type { PastActual } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { TFunction } from 'i18next'

import type { ResolvedConfirmRecordRefColumn } from '~/pages/DemandForecastDaily/models/confirmRecordRef'
import { MAX_CONFIRM_RECORD_REFS } from '~/pages/DemandForecastDaily/models/confirmRecordRef'
import { formatProtoMonthDay } from '~/pages/DemandForecastDaily/models/sectionHelpers'
import { hasDefinedProtoSlotValue } from '~/pages/DemandForecastDaily/models/slotHelpers'

/**
 * 凡例のグループ (8080 `.dft-legend-axis`)。
 *
 * 8080 は凡例を「対象日 × 要素」 の 2 軸に分け、 各軸を枠で囲んで見出しラベルを
 * 付ける。 実装は **系列ごとの個別 ON/OFF を維持したまま枠だけ揃える**
 * (2026-09-01 デザイナー判断)。 8080 の「要素は全対象日に一括作用」 という
 * トグルの効き方は採らない — 過去実績を日付ごとに出し分ける今の粒度が失われるため。
 *
 * - `targetDate` … 需要系列 (需要想定値 / 実績 / 過去実績 M/D / 確定履歴参照)
 * - `element`    … 気象要素系列 (気温 / 気温(実績) / 気温 過去 M/D / 露点)
 * - `trend`      … 実績トレンド予測。 8080 も独立トグルで、 見出しラベルを持たない
 */
export type LegendGroup = 'targetDate' | 'element' | 'trend'

/** legend checkbox 1 系列分の定義。 */
export type LegendItem = {
  key: string
  label: string
  color: string
  /** 需要想定値系列に付与する「編集対象」バッジ (design L2952)。無ければ非表示 */
  badge?: string
  /** 凡例の所属グループ (8080 `.dft-legend-axis`)。 */
  group: LegendGroup
}

/** 系列 key → 色トークンのマッピング (ForecastChart の Line/Area と 1:1 対応させる)。 */
export type ChartLegendColors = {
  forecast: string
  todayActual: string
  trendForecast: string
  tempForecast: string
  tempActual: string
  dewpoint: string
  /** 過去実績スロットの色配列 (index を slot 順に消費、循環利用) */
  past: string[]
  /** 確定履歴参照列の色配列 (index を ref 順に消費、循環利用) */
  confirmRef: string[]
}

export type BuildLegendItemsParams = {
  t: TFunction
  /**
   * 需要想定値系列の凡例ラベル。design L2952 準拠で固定文言「需要想定値」を渡す
   * のが既定 (呼び出し元は `t('page:demandForecastDaily.chart.forecast')` を渡す)。
   */
  forecastLabel: string
  /** 需要想定値系列に「編集対象」バッジを付けるか (design L2952、readonly でない時のみ) */
  showEditableBadge: boolean
  hasTodayActual: boolean
  hasTrendForecast: boolean
  pastActuals: PastActual[]
  /** 確定履歴参照列 (AC-DFD-031)。ForecastChart の confirmRef_N 系列と 1:1 対応させる */
  confirmRecordRefs?: ResolvedConfirmRecordRefColumn[]
  showTemperature: boolean
  hasTempForecast: boolean
  hasTempActual: boolean
  hasDewpointData: boolean
  colors: ChartLegendColors
}

export function buildLegendItems({
  t,
  forecastLabel,
  showEditableBadge,
  hasTodayActual,
  hasTrendForecast,
  pastActuals,
  confirmRecordRefs = [],
  showTemperature,
  hasTempForecast,
  hasTempActual,
  hasDewpointData,
  colors,
}: BuildLegendItemsParams): LegendItem[] {
  const items: LegendItem[] = []

  // 1. 需要想定値 (design L2952: area + 「編集対象」バッジ)
  items.push({
    key: 'forecast',
    label: forecastLabel,
    color: colors.forecast,
    badge: showEditableBadge
      ? t('page:demandForecastDaily.chart.editableBadge')
      : undefined,
    group: 'targetDate',
  })

  // 2. 当日実績 (design L2957: 当日断面のみ)
  if (hasTodayActual) {
    items.push({
      key: 'todayActual',
      label: t('page:demandForecastDaily.chart.todayActual'),
      color: colors.todayActual,
      group: 'targetDate',
    })
  }

  // AC-03: 前日実績系列 (design のビルトイン固定系列) は過剰実装のため削除済み。

  // 4. 過去実績スロット (design L2969: 「実績 M/D」。テーブルヘッダ
  //    (useDemandForecastGridData.ts) と同じ formatProtoMonthDay で統一する)。
  //    需要想定 過去実績「採用」の小売別取込 Design Note Decision 3: 小売別
  //    解決後に値が 1 点も無い日 (分解不能/当該小売の帰属行なし) は凡例項目を
  //    出さない (ForecastChart.tsx の Line 生成と同じ述語
  //    hasDefinedProtoSlotValue を共有し、判定が割れないようにする)。
  for (const [idx, pa] of pastActuals.entries()) {
    if (!hasDefinedProtoSlotValue(pa.slots)) continue
    const dateStr =
      formatProtoMonthDay(pa.date) ??
      t('page:demandForecastDaily.chart.pastActual')
    items.push({
      key: `past_${String(idx)}`,
      label: `${t('page:demandForecastDaily.chart.pastActualPrefix')} ${dateStr}`,
      color: colors.past[idx % colors.past.length] ?? colors.forecast,
      group: 'targetDate',
    })
  }

  // 4a. 確定履歴参照列 (AC-DFD-031: ForecastChart の confirmRef_{id} 系列と同じ
  //     key・順序・上限で凡例項目を生成する。位置は過去実績の直後 (画面右側配置)。
  //     key は ref.id (confirmRecordId, UUID) ベースの stable key
  //     (codex review #5041 should-fix: index ベースだと参照列の削除/クリア後の
  //     再取込で hiddenKeys が別列に「移る」バグを起こすため)。
  for (const [idx, ref] of confirmRecordRefs
    .slice(0, MAX_CONFIRM_RECORD_REFS)
    .entries()) {
    items.push({
      key: `confirmRef_${ref.id}`,
      label: ref.label,
      color:
        colors.confirmRef[idx % colors.confirmRef.length] ?? colors.forecast,
      group: 'targetDate',
    })
  }

  // 4b. 過去実績気温 (AC-024: 選択過去日の実績気温。需要カーブと対比参照する)
  if (showTemperature) {
    for (const [idx, pa] of pastActuals.entries()) {
      if (pa.temperatureSlots.length === 0) continue
      const dateStr =
        formatProtoMonthDay(pa.date) ??
        t('page:demandForecastDaily.chart.pastActual')
      items.push({
        key: `pastTemp_${String(idx)}`,
        label: `${t('page:demandForecastDaily.chart.temperature')} ${t('page:demandForecastDaily.chart.pastActualPrefix')} ${dateStr}`,
        color: colors.past[idx % colors.past.length] ?? colors.forecast,
        group: 'element',
      })
    }
  }

  // 5. 気温系列 (design L2964: 予想気温)
  if (showTemperature && hasTempForecast) {
    items.push({
      key: 'tempForecast',
      label: t('page:demandForecastDaily.chart.temperature'),
      color: colors.tempForecast,
      group: 'element',
    })
  }

  // --- design に無いが実装済みの系列 (末尾に維持) ---
  if (hasTrendForecast) {
    items.push({
      key: 'trendForecast',
      label: t('page:demandForecastDaily.chart.trendForecast'),
      color: colors.trendForecast,
      group: 'trend',
    })
  }
  if (showTemperature && hasTempActual) {
    items.push({
      key: 'tempActual',
      label: t('page:demandForecastDaily.chart.temperatureActual'),
      color: colors.tempActual,
      group: 'element',
    })
  }
  if (showTemperature && hasDewpointData) {
    items.push({
      key: 'dewpoint',
      label: t('page:demandForecastDaily.chart.dewpoint'),
      color: colors.dewpoint,
      group: 'element',
    })
  }

  return items
}

// ===== 露点系列のトグル key 写像 + 過去日露点系列 descriptor (AC-05/AC-06) =====

/**
 * チャート系列の Recharts dataKey → 外部凡例の hiddenKeys トグル key への写像
 * (AC-06)。過去日露点系列 (`pastDew_{idx}`) と対象日実績露点系列
 * (`dewActual`。予測露点は spec #4290 改訂 2026-08-14 で撤去済み) はすべて
 * 単一の凡例チェックボックス ('dewpoint') に相乗りする (design 「露点」凡例
 * 1 項目で実績/過去日をまとめて ON/OFF)。それ以外の dataKey は自身をそのまま
 * トグル key として使う (例: `past_0` → `past_0`)。ForecastCard 内ローカル
 * closure `toggleKey` (setHiddenKeys の更新関数) とは別物 (= key の写像先を
 * 返すだけの純関数)。
 */
export function chartSeriesToggleKey(seriesId: string): string {
  if (seriesId.startsWith('pastDew_') || seriesId === 'dewActual') {
    return 'dewpoint'
  }
  return seriesId
}

/** `pastDewSeriesDescriptors` が返す 1 過去日露点系列の期待値固定点。 */
export type PastDewSeriesDescriptor = {
  id: string
  yAxisId: 'temp'
  toggleKey: string
}

/**
 * 選択過去日群のうち露点実績 (dewpointSlots) を持つ日について、チャート系列の
 * 期待値 (系列 id・割当軸・トグル key) を固定する (AC-05/AC-06, TC-D-40.2)。
 * 露点実績を持たない過去日は descriptor を生成しない (= 系列自体が存在しない
 * こととイコール、AC-07)。「持たない」の判定は配列長でなく定義済みスロットの
 * 有無で行う — 実績未到着の過去日は全コマ欠測の 48 件配列で届くため
 * (`hasDefinedProtoSlotValue` 参照)、長さで見ると値を一点も描けない空の Line を
 * 生成してしまう。`hasDewpointData` と同じ述語を共有することで、凡例・第2軸・
 * 系列の判定が割れないようにする。ForecastChart はこの descriptor 群を map して
 * `<Line>` を生成する — JSX 配線自体の実行検証は F6 fixme 解除まで残らない
 * (§9 残論点、happy-dom で Recharts 系列が DOM 描画されないため)。
 */
export function pastDewSeriesDescriptors(
  pastActuals: PastActual[],
): PastDewSeriesDescriptor[] {
  const out: PastDewSeriesDescriptor[] = []
  for (const [idx, pa] of pastActuals.entries()) {
    if (!hasDefinedProtoSlotValue(pa.dewpointSlots)) continue
    const id = `pastDew_${String(idx)}`
    out.push({ id, yAxisId: 'temp', toggleKey: chartSeriesToggleKey(id) })
  }
  return out
}
